import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import FormInput from "@/components/form-fields/form-input";
import FormDialog from "@/components/reusables/FormDialog";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { Button_Names } from "@/constants/global-constants";
import { PR_Message } from "@/constants/project-register.constant";
import { useDocumentManager } from "@/hooks/useDocumentManager";

type TProps = {
  isOpen: boolean;
  toggleModal: (state: boolean) => void;
  folderFor: string;
  itemId: string;
  currentName: string;
  itemType: "dir" | "document";
};

const renameSchema = z.object({
  name: z.string().trim().min(1, PR_Message.FOLDER_NAME_REQUIRED),
});

type TRenameSchema = z.infer<typeof renameSchema>;

const RenameItem: React.FC<TProps> = ({
  isOpen,
  toggleModal,
  folderFor,
  itemId,
  currentName,
  itemType,
}) => {
  // Extract filename and extension if it's a document
  const fileExtensionRef = useRef<string>("");
  const filenameWithoutExtension = useRef<string>(currentName);
  const form = useForm<TRenameSchema>({
    resolver: zodResolver(renameSchema),
    defaultValues: {
      name: filenameWithoutExtension.current,
    },
  });

  const { handleSubmit, reset } = form;
  const { handleRename } = useDocumentManager({ type: folderFor });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  useEffect(() => {
    if (itemType === "document") {
      const lastDotIndex = currentName.lastIndexOf(".");
      if (lastDotIndex !== -1) {
        fileExtensionRef.current = currentName.substring(lastDotIndex);
        filenameWithoutExtension.current = currentName.substring(
          0,
          lastDotIndex,
        );
      } else {
        fileExtensionRef.current = "";
        filenameWithoutExtension.current = currentName;
      }
    } else {
      filenameWithoutExtension.current = currentName;
      fileExtensionRef.current = "";
    }

    // Update form with filename without extension
    reset({ name: filenameWithoutExtension.current });
  }, [currentName, itemType, reset]);

  const onSubmit = async (data: TRenameSchema): Promise<void> => {
    // Add extension back if it's a document
    const newName =
      itemType === "document"
        ? `${data.name}${fileExtensionRef.current}`
        : data.name;

    // Compare with original name to see if it changed
    if (newName === currentName) {
      toggleModal(false);

      return;
    }

    setIsSubmitting(true);
    try {
      await handleRename(itemId, newName, itemType);
      reset();
      toggleModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = itemType === "dir" ? "Rename Folder" : "Rename Document";

  return (
    <FormDialog
      title={title}
      isOpen={isOpen}
      toggleModal={() => toggleModal(!isOpen)}
      className='max-w-[300px]'
    >
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='my-2 space-y-2'>
            <FormInput
              form={form}
              name='name'
              label={itemType === "dir" ? "Folder Name" : "Document Name"}
              required
              input={{
                type: "text",
                placeholder: `Enter New ${itemType === "dir" ? "Folder" : "Document"} Name`,
              }}
            />
            {itemType === "document" && fileExtensionRef.current && (
              <p className='text-sm text-muted-foreground'>
                Extension: {fileExtensionRef.current}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant={"ghost"}
              type='button'
              onClick={() => {
                reset({ name: filenameWithoutExtension.current });
              }}
            >
              {Button_Names.Reset}
            </Button>
            <Button type='submit' isLoading={isSubmitting}>
              Rename {itemType === "dir" ? "Folder" : "Document"}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default RenameItem;
